import { ref, unref } from 'vue';

/**
 * Export the whole DuckDB database to a zip of parquet files and hand it to the
 * browser as a download.
 *
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn  DuckDB connection (runs EXPORT DATABASE)
 * @param {object|import('vue').Ref} deps.db    DuckDB instance (globFiles / copyFileToBuffer)
 * @returns {{ exporting: import('vue').Ref<boolean>, errorMessage: import('vue').Ref<string>, exportDatabase: () => Promise<void> }}
 */
export function useDuckDBExport({ conn, db }) {
  const exporting = ref(false);
  const errorMessage = ref('');

  const exportDatabase = async () => {
    try {
      exporting.value = true;
      errorMessage.value = '';

      const connection = unref(conn);
      if (!connection) {
        throw new Error('DuckDB connection not initialized');
      }

      // Create a temporary export directory
      const exportDir = `/tmp/export_${Date.now()}`;

      // Export the database to the temporary directory
      await connection.query(`EXPORT DATABASE '${exportDir}' (FORMAT PARQUET)`);

      // Get the list of exported files
      const database = unref(db);
      if (!database) {
        throw new Error('DuckDB instance not available');
      }

      // List the exported files
      const exportedFiles = await database.globFiles(`${exportDir}/*`);
      if (!exportedFiles || exportedFiles.length === 0) {
        throw new Error('No files were exported');
      }

      // Create a zip file containing all the exported files.
      // jszip is imported dynamically so it stays out of the main bundle.
      const JSZip = await import('jszip').then(m => m.default);
      const zip = new JSZip();

      // Add each file to the zip
      for (const file of exportedFiles) {
        try {
          // Use the copyFileToBuffer method mentioned in the GitHub issue
          const buffer = await database.copyFileToBuffer(file.fileName);
          if (buffer) {
            // Add the file to the zip
            const filename = file.fileName.split('/').pop();
            zip.file(filename, buffer);
          }
        } catch (err) {
          console.warn(`Could not add file ${file.fileName} to export: ${err.message}`);
        }
      }

      // Generate the zip file
      const zipBlob = await zip.generateAsync({ type: 'blob' });

      // Download the zip file
      const link = document.createElement('a');
      const url = URL.createObjectURL(zipBlob);

      link.setAttribute('href', url);
      link.setAttribute('download', `duckdb_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.zip`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

    } catch (error) {
      console.error("Database export error:", error);
      errorMessage.value = `Database export error: ${error.message}`;
    } finally {
      exporting.value = false;
    }
  };

  return { exporting, errorMessage, exportDatabase };
}
